import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, HTMLAttributes, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { RiskLevel } from "../../customer/types/queries";
import {
  FormattedCustomerCompany,
  formattedCustomerCompanyDefaultValue,
} from "../orderTypes";
import { OrderType } from "../orderAPI";

type Props = {
  searchHistory: FormattedCustomerCompany[];
  customerCompanies: CustomerOrgCompany[];
  orderTypeValue: OrderType;
  handleOrderTypeValueChange: (type: OrderType) => void;
  handleCustomerCompanyChange: (value: FormattedCustomerCompany) => void;
  handleCustomerCompanyValChange: (value: string) => void;
  resetCustomerCompanies: () => void;
};

const getListItem = (
  props: Omit<HTMLAttributes<HTMLLIElement>, "key">,
  option: any,
  lastSearched: boolean,
  t: Function,
) => {
  const salesPermitProps: any = {
    YELLOW: {
      label: t("salesRestriction"),
      color: "warning",
    },
    RED: {
      label: t("noSales"),
      color: "error",
    },
    UNKNOWN: {
      label: t("riskUnknown"),
      color: "warning",
    },
  };

  const showSalesPermitRestrictionChip =
    !!option.salesPermit && option.salesPermit !== RiskLevel.GREEN;

  return (
    <Fragment key={option.orgId}>
      {option.index === 0 && (
        <ListSubheader
          sx={{ textTransform: "uppercase", color: "#9167CA", fontWeight: 600 }}
        >
          <Typography variant="header">{t("previousCustomers")}</Typography>
        </ListSubheader>
      )}
      <ListItem {...props}>
        <ListItemText
          primary={
            <Typography
              sx={{ textTransform: lastSearched ? "uppercase" : "unset" }}
            >
              {option.company}
              {showSalesPermitRestrictionChip && (
                <Box component="span" sx={{ marginLeft: "0.5rem" }}>
                  <Chip
                    sx={{ fontWeight: 600 }}
                    {...salesPermitProps[option.salesPermit]}
                    variant="outlined"
                  />
                </Box>
              )}
            </Typography>
          }
        />
      </ListItem>
    </Fragment>
  );
};

const OrderFormBasic = ({
  searchHistory,
  customerCompanies,
  handleOrderTypeValueChange,
  orderTypeValue,
  handleCustomerCompanyChange,
  handleCustomerCompanyValChange,
  resetCustomerCompanies,
}: Props) => {
  const { t } = useTranslation(["order", "common"]);

  const formattedAutocompleteData = useMemo(() => {
    return customerCompanies.flatMap((item) =>
      item.customerOrganizations.map((org) => ({
        org: item.name,
        company: org.name,
        orgId: org.id,
        salesPermit: org.salesPermission?.riskLevel,
      })),
    );
  }, [customerCompanies]);

  const showLastSearched = !formattedAutocompleteData.length;
  const options = showLastSearched ? searchHistory : formattedAutocompleteData;

  return (
    <>
      <Typography>{t("order:selectCustomer")}</Typography>
      <FormControl fullWidth margin="normal">
        {/* TODO: use CustomerOrgCompanyAutocomplete */}
        <Autocomplete
          fullWidth
          disablePortal
          ListboxProps={{ style: { maxHeight: "300px", overflow: "visible" } }}
          isOptionEqualToValue={(option, value) =>
            option.company === value.company
          }
          // show last searched companies, disable grouping
          {...(!!formattedAutocompleteData.length && {
            groupBy: (option: any) => option.org,
          })}
          getOptionLabel={(option) => option.company}
          filterOptions={createFilterOptions({
            stringify: (option) => JSON.stringify(option),
          })}
          options={options}
          onChange={(_, newValue: FormattedCustomerCompany | null) => {
            if (newValue) {
              handleCustomerCompanyChange(newValue);
            } else {
              handleCustomerCompanyChange(formattedCustomerCompanyDefaultValue);
              resetCustomerCompanies();
            }
          }}
          onInputChange={(_, newValue: string) => {
            if (newValue) {
              handleCustomerCompanyValChange(newValue);
            } else {
              resetCustomerCompanies();
              handleCustomerCompanyChange(formattedCustomerCompanyDefaultValue);
            }
          }}
          renderOption={(props, option) =>
            getListItem(props, option, !formattedAutocompleteData.length, t)
          }
          renderGroup={({ group, children }) => (
            <Fragment key={group}>
              <ListSubheader
                disableSticky
                sx={{
                  color: "#9167CA",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: "20px",
                }}
              >
                <Typography variant="header">{group}</Typography>
              </ListSubheader>
              <List sx={{ paddingLeft: "32px" }}>{children}</List>
            </Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("common:customerCompany")}
              name="customerOrgCompany"
              required
            />
          )}
        />
      </FormControl>
      <FormControl margin="normal">
        <FormLabel>
          <Typography
            variant="header"
            sx={{ fontWeight: 600, textTransform: "uppercase" }}
          >
            {t("order:workingHours")}
          </Typography>
        </FormLabel>
        <RadioGroup
          name="orderTypeValue"
          onChange={(e) =>
            handleOrderTypeValueChange(e.target.value as OrderType)
          }
          value={orderTypeValue}
        >
          <FormControlLabel
            value={OrderType.Short}
            control={<Radio />}
            label={t("order:shortOrderLabel")}
          />
          <FormControlLabel
            value={OrderType.FixedTerm}
            control={<Radio />}
            label={t("order:fixedTermOrderLabel")}
          />
          <FormControlLabel
            value={OrderType.Continuous}
            control={<Radio />}
            label={t("order:continuousOrderLabel")}
          />
        </RadioGroup>
      </FormControl>
    </>
  );
};

export default OrderFormBasic;
